import {
    signInWithRedirect,
    signOut,
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
    resetPassword,
    confirmResetPassword,
    setUpTOTP,
    updateMFAPreference,
    UpdateMFAPreferenceInput,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { loginParams, UserInfo } from './types';
import { get } from 'lodash';
import { LocalStorageKey, setItem } from 'services/localStorageService';

interface ITokens {
    accessToken: string | undefined;
    expiration: string | undefined;
}

const channels = ['auth', 'UI Auth', 'core', 'oauth', '*'];

// Event handler for all Hub events
channels.forEach((channel) => {
    Hub.listen(channel, (data) => {
        console.log(`[${channel}] Event:`, data);
        const { payload } = data;

        if (!payload) {
            console.warn(`[${channel}] No payload in event:`, data);
            return;
        }

        const { event, data: eventData } = payload;

        // Handle specific events
        switch (channel) {
            case 'auth':
                switch (event) {
                    // Success Events
                    case 'signIn':
                        break;

                    case 'signOut':
                        break;

                    case 'tokenRefresh':
                        // Could update store or token state here if needed
                        break;

                    // Failure Events
                    case 'signIn_failure':
                        break;

                    case 'tokenRefresh_failure':
                        break;

                    case 'signOut_failure':
                        break;

                    // Edge Cases
                    case 'forgotPassword':
                    case 'forgotPasswordSubmit':
                        console.log(`[${channel}] Password reset event:`, event, eventData);
                        // Handle password reset flow if needed
                        break;

                    case 'customOAuthState':
                        console.log(`[${channel}] Custom OAuth event:`, eventData);
                        break;

                    default:
                        console.log(`[${channel}] Unhandled auth event:`, event, payload);
                        break;
                }
                break;

            // Add other channels if needed (e.g., 'storage', 'api')
            default:
                console.log(`[${channel}] Event from unhandled channel:`, payload);
                break;
        }
    });
});

class AuthService {
    private isRedirecting: boolean = false;
    private tokens: object | undefined = undefined;
    constructor() {
        this.setupAuthListener();
    }

    public async setSession(tokens) {
        if (tokens) {
            const { accessToken, expiration } = tokens;
            if (accessToken) setItem(LocalStorageKey.ACCESS_TOKEN, accessToken);
            if (expiration) setItem(LocalStorageKey.TOKEN_EXPIRATION_DATE, expiration);
        }
    }

    /**
     * Fetch tokens
     * @returns
     */
    public async getAuthTokens(): Promise<ITokens | null> {
        try {
            const session = await fetchAuthSession();
            if (!session.tokens?.accessToken) {
                return null;
            }
            const expirationTime = get(session, 'tokens.accessToken.payload.exp');
            const tokens = {
                accessToken: session.tokens?.accessToken?.toString(),
                expiration: expirationTime ? new Date(expirationTime * 1000).toISOString() : undefined,
            };
            this.tokens = tokens;
            return tokens;
        } catch (error) {
            console.error('Error getting tokens:', error);
            this.tokens = undefined;
            return null;
        }
    }

    // Set up Hub listener in your service constructor or initialization method
    private setupAuthListener() {
        console.log('Auth listener set up successfully');
    }
    /**
     * Login signInWithRedirect
     * @returns
     */
    public async login(params: loginParams): Promise<ITokens | null | void> {
        this.setupAuthListener();
        const isAuthenticated = await this.isAuthenticated();
        // Avoid to login again if the user is already connected
        if (isAuthenticated) {
            const tokens = await this.getAuthTokens();
            return tokens;
        }

        if (this.isRedirecting || isAuthenticated) {
            return;
        }

        try {
            this.isRedirecting = true;
            await signInWithRedirect({
                options: {
                    preferPrivateSession: true,
                    lang: params.lang,
                },
            });

            this.isRedirecting = false;
            // You can check for tokens immediately after redirect
            const tokens = await this.getAuthTokens();
            if (tokens) {
                return tokens;
            }
        } catch (error) {
            console.log(error);
            this.isRedirecting = false;
        }
    }

    /**
     * Logout
     */
    public async logout({
        cleanSessionInfoCallback,
        afterLogoutEvent,
    }: {
        cleanSessionInfoCallback?: () => void;
        afterLogoutEvent?: () => void;
    }): Promise<void> {
        if (this.isRedirecting) {
            return;
        }

        try {
            this.isRedirecting = true;
            if (typeof cleanSessionInfoCallback === 'function') {
                cleanSessionInfoCallback();
            }
            await signOut({ global: false });
            if (afterLogoutEvent) {
                afterLogoutEvent();
            }
        } catch (error) {
        } finally {
            this.isRedirecting = false;
        }
    }

    /**
     * isAuthenticated
     * @returns
     */
    public isAuthenticated = async (): Promise<boolean> => {
        try {
            const session = await fetchAuthSession();
            const expirationTime = session.tokens?.accessToken?.payload.exp;
            if (expirationTime) {
            }
            return !!session.tokens?.accessToken;
        } catch {
            return false;
        }
    };

    public async getUserInfo(): Promise<any> {
        try {
            // Get both user attributes and current user info
            const [attributes, currentUser] = await Promise.all([fetchUserAttributes(), getCurrentUser()]);

            return {
                attributes,
                userId: currentUser.userId,
                username: currentUser.username,
                signInDetails: currentUser.signInDetails,
            };
        } catch (error) {
            console.log('Get user info error:', error);
            return {};
        }
    }

    public async resetUserPassword(username: string): Promise<void> {
        try {
            await resetPassword({ username });
        } catch (error) {
            console.error('Reset password error:', error);
            throw error;
        }
    }

    public async confirmPasswordReset(username: string, newPassword: string, confirmationCode: string): Promise<void> {
        try {
            await confirmResetPassword({
                username,
                newPassword,
                confirmationCode,
            });
        } catch (error) {
            console.error('Confirm reset password error:', error);
            throw error;
        }
    }

    public async setupTOTP(): Promise<string> {
        try {
            const totpSetupDetails = await setUpTOTP();
            return totpSetupDetails.sharedSecret;
        } catch (error) {
            console.error('Setup TOTP error:', error);
            throw error;
        }
    }

    public async updateMFAPreferences(input: UpdateMFAPreferenceInput): Promise<void> {
        try {
            await updateMFAPreference(input);
        } catch (error) {
            console.error('Update preference MFA', error);
            throw error;
        }
    }

    public async refreshSession(callbackResponse?: (params: any) => void) {
        try {
            // Force refresh the session
            const { tokens } = await fetchAuthSession({ forceRefresh: true });

            if (!tokens) {
                this.tokens = undefined;
                throw new Error('No tokens returned from session refresh');
            }

            // Extract the access token
            const { accessToken } = tokens;

            // Get the JWT token string
            const token = accessToken.toString();

            // Parse JWT payload (without using external libraries)
            const payload = accessToken.payload;

            // Extract timestamp information (in seconds)
            const issuedAt = payload.iat;
            const expiration = payload.exp;

            // Check if the token contains the required fields to calculate the emission and expiration dates.
            if (issuedAt === undefined || expiration === undefined) {
                this.tokens = undefined;
                throw new Error('Token does not contain required fields');
            }

            // Create token information object
            const expirationTokenInfo = {
                emissionDate: new Date(issuedAt * 1000).toISOString(),
                expirationDate: new Date(expiration * 1000).toISOString(),
                expireIn: expiration - Math.floor(Date.now() / 1000),
            };

            const response = {
                emissionDate: expirationTokenInfo.emissionDate,
                expirationDate: expirationTokenInfo.expirationDate,
                expiresIn: expirationTokenInfo.expireIn,
                token,
            };

            if (callbackResponse) {
                return callbackResponse(response);
            }

            return response;
        } catch (error) {
            console.error('Error refreshing session:', error);
            this.tokens = undefined;
            throw error;
        }
    }

    public checkSession = async () => {
        try {
            const session = await fetchAuthSession({ forceRefresh: true });
            return !!session.tokens;
        } catch (error) {
            console.error('❌ User is logged out or session is invalid:', error);
            return false; // Session is revoked or expired
        }
    };
}

const authCognito = new AuthService();
export default authCognito;
